import { CommonModule } from '@angular/common'
import { Component, OnDestroy, OnInit, computed, inject, input, signal } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { ActivatedRoute, Router, RouterLink } from '@angular/router'
import { MessageService } from '@openng/optimus-ui/api'
import { Subscription, finalize } from 'rxjs'
import { OptimUiSharedModule } from '../../../shared/optimus-ui-shared.module'
import { ClassManagerApiService } from '../../../_services/system_api/classManager/api.service'
import { AwpMediationWorkflowApiService, AwpVoipApiService, WorkflowManagerApiService } from '../../../_services'
import { buildRunRequestPayload, cloneDefinition, setActionCatalog } from '../../perimetroAWP_mediation/workflow/workflow-catalog'
import type { WorkflowDefinition } from '../../perimetroAWP_mediation/workflow/workflow.model'
import { SiteLiveTunnels, SiteOverview, SiteTimelineApiService } from '../../../_services'
import { FqTunnel, FqTunnelStatusComponent } from '../../../shared/fq-tunnel-status.component'
import { SiteTimelineComponent } from '../site-timeline/site-timeline'
import { isVlanMetafieldName } from '../../../_helper/vlan-format.util'
import { containsHtml, stripHtml } from '../../../_helper/html-format.util'
import { warningSourceLabel, warningSourceLabels } from '../../../_helper/data-warning.util'
import { SdbTagsStore } from '../../../_shared/sdb-tags/sdb-tags.store'
import { SdbTagsComponent } from '../../../_shared/sdb-tags/sdb-tags.component'
import { FqDialogNavComponent, FqDialogNavSection } from '../../../shared/fq-dialog-nav.component'
import { SupportTicketFormComponent } from '../../../_shared/support/support-ticket-form.component'
import { FuseqSessionService } from '../../../FuseQ/session.service'
import { SpeedtestHistoryTableComponent } from '../../speedTestAWP/speedtest-history-table/speedtest-history-table.component'
import { VlanTableComponent } from '../../../_shared/vlan-table/vlan-table.component'
import { MediationRunsComponent } from '../../perimetroAWP_mediation/runs/mediation-runs.component'

type HubTab = 'timeline' | 'tickets' | 'perimetro' | 'circuiti' | 'metafield' | 'workflow' | 'dispositivi' | 'speedtest'

@Component({
  selector: 'app-site-hub',
  standalone: true,
  imports: [OptimUiSharedModule, CommonModule, FormsModule, RouterLink, SiteTimelineComponent, FqTunnelStatusComponent, SdbTagsComponent, FqDialogNavComponent, SupportTicketFormComponent, SpeedtestHistoryTableComponent, VlanTableComponent, MediationRunsComponent],
  templateUrl: './site-hub.html',
  styleUrls: ['./site-hub.scss']
})
export class SiteHubComponent implements OnInit, OnDestroy {
  private readonly api = inject(SiteTimelineApiService)
  private readonly classApi = inject(ClassManagerApiService)
  private readonly workflowApi = inject(WorkflowManagerApiService)
  private readonly mediationWorkflowApi = inject(AwpMediationWorkflowApiService)
  private readonly voipApi = inject(AwpVoipApiService)
  private readonly sdbTagsStore = inject(SdbTagsStore)
  private readonly fuseqSession = inject(FuseqSessionService)
  private readonly messageService = inject(MessageService)
  private readonly route = inject(ActivatedRoute)
  private readonly router = inject(Router)
  private readonly subscriptions = new Subscription()

  /** Frazionario passato dal contenitore (es. dialog Info sede). */
  readonly frazInput = input<string | null>(null)
  /** Modalita' incorporata: niente selettore sede ne' sincronizzazione dei tab sulla URL. */
  readonly embedded = input(false)
  /** Tab iniziale in modalita' incorporata. */
  readonly initialTab = input<HubTab | null>(null)

  readonly fraz = signal('')
  readonly selectedSite = signal('')
  readonly sites = signal<{ label: string, value: string }[]>([])
  readonly overview = signal<SiteOverview | null>(null)
  readonly devices = signal<any[]>([])
  readonly devicesLoading = signal(false)
  readonly workflowTemplates = signal<WorkflowDefinition[]>([])
  readonly selectedWorkflowTemplateId = signal('')
  readonly workflowCatalogReady = signal(false)
  readonly workflowTemplatesLoading = signal(false)
  readonly workflowRunStarting = signal(false)
  private workflowLauncherLoaded = false

  readonly voipAssets = signal<any[]>([])
  readonly voipLines = signal<any[]>([])
  readonly voipSondes = signal<any[]>([])
  readonly voipFaxes = signal<any[]>([])
  readonly voipLoading = signal(false)
  readonly voipError = signal<string | null>(null)
  private voipLoadedFraz = ''
  private voipRequestToken = 0
  private loadToken = 0
  private loadingFraz = ''
  readonly pingSpans = signal<any[]>([])
  readonly pingLoading = signal(false)
  readonly diagnosis = signal<any | null>(null)
  readonly diagnosisLoading = signal(false)
  readonly liveTunnels = signal<SiteLiveTunnels | null>(null)
  readonly liveTunnelsLoading = signal(false)
  readonly pingWindowDays = 30
  readonly closingBlocking = signal(false)
  readonly ticketDialogVisible = signal(false)
  readonly loading = signal(false)
  readonly error = signal<string | null>(null)
  readonly tab = signal<HubTab>('perimetro')

  readonly siteFraz = computed(() => this.overview()?.site?.fraz || this.fraz())

  /** Scheda sede in anteprima: visibile solo con il permesso di monitoraggio code. */
  readonly canView = computed(() => {
    const permissions: string[] = this.fuseqSession.session?.permissions ?? []
    return permissions.includes('MENU_QUEUE_MONITORING')
  })
  readonly siteSdbTags = computed(() => this.sdbTagsStore.tags(this.siteFraz()))
  readonly canManage = computed(() => this.overview()?.tickets?.canManage === true)
  readonly blocking = computed(() => this.overview()?.rtu?.row?.blocking === true)
  readonly nrdConstraint = computed(() => this.overview()?.nrd?.constraintAt || null)
  readonly siteDevices = computed(() => {
    const fraz = String(this.siteFraz() || '').trim().toUpperCase()
    if (!fraz) {
      return []
    }
    return this.devices().filter((device) => String(device?.Frazionario || '').trim().toUpperCase() === fraz)
  })
  readonly voipInstalledCount = computed(() =>
    this.voipAssets().filter((asset) => this.isVoipInstalled(asset)).length
  )
  /** Fax server ordinati per numero (confronto numerico, non lessicografico). */
  readonly faxesSorted = computed(() => {
    const collator = new Intl.Collator('it', { numeric: true, sensitivity: 'base' })
    const number = (fax: any) => String(fax?.FaxNumber ?? fax?.Telefono ?? fax?.Code ?? '')
    return [...(this.voipFaxes() ?? [])].sort((a, b) => collator.compare(number(a), number(b)))
  })
  readonly health = computed<{ status: 'ok' | 'warning' | 'critical' | 'na', label: string, reasons: string[] }>(() => {
    const reasons: string[] = []
    const overview = this.overview()
    if (!overview) {
      return { status: 'ok', label: 'OK', reasons }
    }

    const hasRtu = Boolean(overview.rtu?.row)
    // Sede fuori dal perimetro SDWAN/NRD: la RTU può legittimamente non esserci.
    const outOfPerimeter = !hasRtu && this.technology() === 'NONE' && !this.sdwanFlag() && !this.nrdInPerimeter()

    if (this.blocking()) {
      reasons.push('RTU in blocco')
    }
    if (!hasRtu) {
      if (!outOfPerimeter) {
        reasons.push('Nessuna RTU censita')
      }
    } else if (overview.rtu?.lastPing?.state === 'offline') {
      reasons.push('RTU offline')
    } else if (!overview.rtu?.lastPing) {
      reasons.push('Stato RTU sconosciuto')
    }
    const down = this.siteDevices().filter((device) => device?.Status === 'down')
    if (down.length > 0) {
      reasons.push(`${down.length} apparato/i offline`)
    }
    const unknown = this.siteDevices().filter((device) => device?.Status !== 'up' && device?.Status !== 'down')
    if (unknown.length > 0) {
      reasons.push(`${unknown.length} apparato/i senza stato`)
    }
    if (/fail|error|errore/i.test(String(overview.speedtest?.status || ''))) {
      reasons.push('Ultimo speedtest fallito')
    }
    if (this.nrdConstraint()) {
      reasons.push('Vincolo NRD')
    }

    const critical = this.blocking() || overview.rtu?.lastPing?.state === 'offline' || down.length > 0
    if (critical) {
      return { status: 'critical', label: 'Critico', reasons }
    }
    if (reasons.length > 0) {
      return { status: 'warning', label: 'Attenzione', reasons }
    }
    if (outOfPerimeter) {
      return { status: 'na', label: 'N/D', reasons: ['Sede non in perimetro: nessuna RTU censita'] }
    }
    return { status: 'ok', label: 'OK', reasons }
  })
  readonly ticketsOpen = computed(() => {
    const counts = this.overview()?.tickets?.counts || {}
    return (counts['open'] || 0) + (counts['in_progress'] || 0)
  })

  /** Fonti che non hanno risposto, in forma leggibile (banner warning). */
  readonly warningLabels = computed(() => warningSourceLabels(this.overview()?.warnings ?? []))

  /** Dettaglio del warning: fonte leggibile + messaggio del backend. */
  readonly warningDetails = computed(() => (this.overview()?.warnings ?? [])
    .map((warning) => ({
      label: warningSourceLabel(warning.name),
      message: this.cleanText(warning.message)
    }))
    .filter((warning) => warning.message !== ''))

  /** Tecnologia SDWAN della sede: Cisco, Fortinet, entrambe o nessuna. */
  readonly technology = computed<'CISCO' | 'FORTINET' | 'BOTH' | 'NONE'>(() => {
    const overview = this.overview()
    const cisco = Boolean(overview?.sdwan?.cisco)
    const fortinet = Boolean(overview?.sdwan?.fortinet)
    if (cisco && fortinet) {
      return 'BOTH'
    }
    if (cisco) {
      return 'CISCO'
    }
    if (fortinet) {
      return 'FORTINET'
    }
    return 'NONE'
  })

  /** Flag SDWAN dell'anagrafica, normalizzato anche se arriva come stringa. */
  readonly sdwanFlag = computed(() => {
    const flag = this.overview()?.site?.sdwan
    if (typeof flag === 'boolean') {
      return flag
    }
    if (typeof flag === 'string') {
      return !['', 'no', 'false', '0', 'n'].includes(flag.trim().toLowerCase())
    }
    return Boolean(flag)
  })

  readonly technologyLabel = computed(() => {
    switch (this.technology()) {
      case 'CISCO':
        return 'Cisco'
      case 'FORTINET':
        return 'Fortinet'
      case 'BOTH':
        return 'Cisco + Fortinet'
      default:
        return this.sdwanFlag() ? 'SDWAN' : 'Nessuna SDWAN'
    }
  })

  /** Cella SDWAN della sede (preferenza Cisco, come `mat_PerimetroAWP`). */
  readonly cella = computed(() => {
    const overview = this.overview()
    return overview?.sdwan?.cisco?.cella || overview?.sdwan?.fortinet?.cella || ''
  })

  /** SIM associate agli apparati SDWAN (Fortinet/Cisco). */
  readonly sims = computed(() => {
    const overview = this.overview()
    const list: { technology: string, device: string, sim: string, tgu: string | null, switchIp: string | null }[] = []
    const cisco = overview?.sdwan?.cisco
    if (cisco?.sim) {
      list.push({
        technology: 'Cisco',
        device: cisco.ciscoEdge || cisco.edgeHostname || 'CiscoEdge',
        sim: cisco.sim,
        tgu: cisco.tgu || null,
        switchIp: cisco.switchIp || null
      })
    }
    const fortinet = overview?.sdwan?.fortinet
    if (fortinet?.sim) {
      list.push({
        technology: 'Fortinet',
        device: fortinet.fex || fortinet.fortigate || 'Fortinet',
        sim: fortinet.sim,
        tgu: fortinet.tgu || null,
        switchIp: fortinet.switchIp || null
      })
    }
    return list
  })

  /** Linee cessate registrate nella coda NRD. */
  readonly ceasedLines = computed(() => this.overview()?.nrdCeasedLines ?? [])

  readonly sdwanMigratedAt = computed<string | null>(() => {
    const overview = this.overview()
    return overview?.site?.dataMigrazioneSdwan
      || overview?.sdwan?.fortinet?.migratedAt
      || overview?.sdwan?.cisco?.migratedAt
      || null
  })

  readonly sdwanMigrated = computed(() => Boolean(this.sdwanMigratedAt()))

  readonly sdwanPlannedAt = computed<string | null>(() => {
    const overview = this.overview()
    return overview?.sdwan?.fortinet?.migrationPlanned
      || overview?.sdwan?.cisco?.migrationPlanned
      || null
  })

  readonly sdwanStatusLabel = computed(() => {
    if (this.sdwanMigrated()) {
      return 'Migrato SDWAN'
    }
    if (this.technology() === 'NONE' && !this.sdwanFlag()) {
      return 'SDWAN non in perimetro'
    }
    return 'Non migrato SDWAN'
  })

  readonly sdwanStatusDetail = computed(() => {
    if (this.sdwanMigratedAt()) {
      return `Migrato il ${this.formatDateOnly(this.sdwanMigratedAt())}`
    }
    if (this.sdwanPlannedAt()) {
      return `Pianificato ${this.formatDateOnly(this.sdwanPlannedAt())}`
    }
    return ''
  })

  readonly nrdInPerimeter = computed(() => Boolean(this.overview()?.nrd))

  readonly nrdMigratedAt = computed<string | null>(() => this.overview()?.nrd?.migratedAt || null)

  readonly nrdMigrated = computed(() => Boolean(this.nrdMigratedAt()))

  readonly nrdStatusLabel = computed(() => {
    if (!this.nrdInPerimeter()) {
      return 'NRD non in perimetro'
    }
    return this.nrdMigrated() ? 'Migrato NRD' : 'Non migrato NRD'
  })

  readonly nrdStatusDetail = computed(() => {
    if (this.nrdMigratedAt()) {
      return `Migrato il ${this.formatDateOnly(this.nrdMigratedAt())}`
    }
    const planned = this.overview()?.nrd?.planned
    if (planned) {
      return `Pianificato ${this.formatDateOnly(planned)}`
    }
    return ''
  })

  technologyStatusClass (): string {
    switch (this.technology()) {
      case 'CISCO':
        return 'is-cisco'
      case 'FORTINET':
        return 'is-fortinet'
      case 'BOTH':
        return 'is-mixed'
      default:
        return 'is-none'
    }
  }

  technologyIcon (): any {
    switch (this.technology()) {
      case 'CISCO':
      case 'FORTINET':
      case 'BOTH':
        return ['fas', 'network-wired']
      default:
        return ['fas', 'minus']
    }
  }

  sdwanStatusClass (): string {
    if (this.sdwanMigrated()) {
      return 'is-ok'
    }
    return this.technology() === 'NONE' && !this.sdwanFlag() ? 'is-none' : 'is-warn'
  }

  sdwanStatusIcon (): any {
    if (this.sdwanMigrated()) {
      return ['fas', 'circle-check']
    }
    return this.technology() === 'NONE' && !this.sdwanFlag()
      ? ['fas', 'minus']
      : ['fas', 'circle-exclamation']
  }

  nrdStatusClass (): string {
    if (!this.nrdInPerimeter()) {
      return 'is-none'
    }
    return this.nrdMigrated() ? 'is-ok' : 'is-warn'
  }

  nrdStatusIcon (): any {
    if (!this.nrdInPerimeter()) {
      return ['fas', 'minus']
    }
    return this.nrdMigrated() ? ['fas', 'circle-check'] : ['fas', 'circle-exclamation']
  }

  /** Sezioni della scheda, mostrate come pill con icona (`fq-dialog-nav`). */
  readonly tabs = computed<FqDialogNavSection[]>(() => {
    const tickets = this.ticketsOpen()
    return [
      { key: 'perimetro', label: 'Perimetro', icon: ['fas', 'network-wired'] },
      { key: 'dispositivi', label: 'Devices', icon: ['fas', 'server'] },
      { key: 'circuiti', label: 'Circuiti', icon: ['fas', 'sitemap'] },
      { key: 'metafield', label: 'Metafield', icon: ['fas', 'tags'] },
      { key: 'workflow', label: 'Workflow', icon: ['fas', 'diagram-project'] },
      { key: 'speedtest', label: 'Speedtest', icon: ['fas', 'gauge-high'] },
      { key: 'timeline', label: 'Timeline', icon: ['fas', 'timeline'] },
      { key: 'tickets', label: tickets > 0 ? `Ticket AWP (${tickets})` : 'Ticket AWP', icon: ['fas', 'circle-info'] }
    ]
  })

  ngOnInit (): void {
    if (!this.canView()) {
      return
    }
    this.loadWorkflowLauncherData()
    const inputFraz = String(this.frazInput() ?? '').trim()
    if (inputFraz) {
      this.fraz.set(inputFraz)
      const initialTab = this.initialTab()
      if (initialTab && this.tabs().some((item) => item.key === initialTab)) {
        this.tab.set(initialTab)
      }
      this.load(inputFraz)
      return
    }
    this.loadSites()
    this.subscriptions.add(this.route.paramMap.subscribe((params) => {
      const fraz = params.get('fraz') || ''
      if (fraz === this.fraz() && this.overview()) {
        return
      }
      this.fraz.set(fraz)
      this.overview.set(null)
      this.error.set(null)
      if (fraz) {
        this.load(fraz)
      }
    }))
    const tab = this.route.snapshot.queryParamMap.get('tab') as HubTab | null
    if (tab && this.tabs().some((item) => item.key === tab)) {
      this.tab.set(tab)
    }
  }

  ngOnDestroy (): void {
    this.subscriptions.unsubscribe()
  }

  loadSites (): void {
    this.classApi.getAnagraficaSiti().subscribe({
      next: (rows) => {
        const options = (Array.isArray(rows) ? rows : [])
          .filter((row) => row && row.fraz)
          .map((row) => ({
            label: `${row.fraz}${row.denominazione ? ' — ' + row.denominazione : ''}`,
            value: String(row.fraz)
          }))
          .sort((a, b) => a.label.localeCompare(b.label))
        this.sites.set(options)
      },
      error: () => { /* campo digitabile */ }
    })
  }

  load (fraz: string): void {
    if (!this.canView()) {
      return
    }
    const key = String(fraz || '').trim()
    if (!key) {
      return
    }
    if (this.loading() && this.loadingFraz === key) {
      return
    }
    const token = ++this.loadToken
    this.loadingFraz = key
    this.loading.set(true)
    this.liveTunnels.set(null)
    this.voipRequestToken += 1
    this.voipLoading.set(false)
    this.voipLoadedFraz = ''
    this.voipAssets.set([])
    this.voipLines.set([])
    this.voipSondes.set([])
    this.voipFaxes.set([])
    this.voipError.set(null)
    this.loadDevices(key)
    this.api.getOverview(key).subscribe({
      next: (data) => {
        if (token !== this.loadToken) {
          return
        }
        this.overview.set(data)
        this.loading.set(false)
        this.loadingFraz = ''
        const siteKey = data?.site?.fraz || key
        this.sdbTagsStore.ensure([siteKey])
        if (data?.rtu?.row) {
          this.loadPing(siteKey)
        } else {
          this.pingSpans.set([])
          this.diagnosis.set(null)
        }
        if (this.tab() === 'perimetro' || this.tab() === 'circuiti' || this.tab() === 'dispositivi') {
          this.loadVoipDetails()
        }
      },
      error: (err) => {
        if (token !== this.loadToken) {
          return
        }
        this.loading.set(false)
        this.loadingFraz = ''
        const detail = err?.error?.message ?? 'Errore durante il caricamento della sede'
        this.error.set(String(detail))
        if (!err.isintercepted) {
          this.messageService.add({ severity: 'error', summary: 'Sede', detail: String(detail) })
        }
      }
    })
  }

  openSite (): void {
    const fraz = this.selectedSite().trim()
    if (fraz) {
      void this.router.navigate(['/awp/sites', fraz])
    }
  }

  loadDevices (fraz: string): void {
    if (!fraz) {
      return
    }
    this.devices.set([])
    this.devicesLoading.set(true)
    this.workflowApi.GetDevicesStatus().subscribe({
      next: (data) => {
        this.devices.set(Array.isArray(data) ? data : [])
        this.devicesLoading.set(false)
      },
      error: () => {
        this.devices.set([])
        this.devicesLoading.set(false)
      }
    })
  }

  /**
   * Apparati, linee, sonde 2N e fax VoIP della sede. Caricati solo quando
   * serve il tab Perimetro/Dispositivi (o su refresh), per non appesantire
   * l'apertura della scheda.
   */
  loadVoipDetails (force = false): void {
    const fraz = String(this.siteFraz() || this.fraz() || '').trim()
    if (!fraz || this.voipLoading()) {
      return
    }
    if (!force && this.voipLoadedFraz === fraz) {
      return
    }
    const token = ++this.voipRequestToken
    this.voipLoadedFraz = fraz
    this.voipLoading.set(true)
    this.voipError.set(null)

    this.voipApi.Get_VOIPSiteDetails(fraz).pipe(finalize(() => {
      if (token === this.voipRequestToken) {
        this.voipLoading.set(false)
      }
    })).subscribe({
      next: (response) => {
        if (token !== this.voipRequestToken) {
          return
        }
        this.voipAssets.set(Array.isArray(response?.assets) ? response.assets : [])
        this.voipLines.set(Array.isArray(response?.lines) ? response.lines : [])
        this.voipSondes.set(Array.isArray(response?.sondes) ? response.sondes : [])
        this.voipFaxes.set(Array.isArray(response?.faxes) ? response.faxes : [])
      },
      error: () => {
        if (token !== this.voipRequestToken) {
          return
        }
        this.voipLoadedFraz = ''
        this.voipAssets.set([])
        this.voipLines.set([])
        this.voipSondes.set([])
        this.voipFaxes.set([])
        this.voipError.set('Dettagli VoIP non disponibili')
      }
    })
  }

  isVoipInstalled (asset: any): boolean {
    const esito = String(asset?.Esito ?? '').trim().toLowerCase()
    const config = String(asset?.ConfigReteOK ?? '').trim().toLowerCase()
    return esito === 'completato' || esito === 'installato' || config === 'ok'
  }

  openTicketDialog (): void {
    this.ticketDialogVisible.set(true)
  }

  /** Ticket creato: chiude il dialog e ricarica l'overview per ticket e contatori. */
  onTicketCreated (): void {
    this.ticketDialogVisible.set(false)
    const fraz = this.siteFraz()
    if (!fraz) {
      return
    }
    this.api.getOverview(fraz).subscribe({
      next: (data) => this.overview.set(data),
      error: () => { /* la scheda resta invariata */ }
    })
  }

  closeBlocking (): void {
    const fraz = this.siteFraz()
    if (!fraz || this.closingBlocking()) {
      return
    }
    this.closingBlocking.set(true)
    this.classApi.closeSegnalazioneRTU(fraz).subscribe({
      next: () => {
        this.closingBlocking.set(false)
        this.messageService.add({ severity: 'success', summary: 'Sede', detail: 'Blocco RTU chiuso' })
        this.load(fraz)
      },
      error: (err) => {
        this.closingBlocking.set(false)
        const detail = err?.error?.message ?? 'Errore durante la chiusura del blocco'
        this.messageService.add({ severity: 'error', summary: 'Sede', detail: String(detail) })
      }
    })
  }

  healthClass (): string {
    switch (this.health().status) {
      case 'critical':
        return 'fq-badge-danger'
      case 'warning':
        return 'fq-badge-warning'
      case 'na':
        return 'fq-badge-secondary'
      default:
        return 'fq-badge-success'
    }
  }

  deviceBadgeClass (status: string | null | undefined): string {
    if (status === 'up') {
      return 'fq-badge-success'
    }
    if (status === 'down') {
      return 'fq-badge-danger'
    }
    return 'fq-badge-secondary'
  }

  deviceLabel (device: any): string {
    const name = device?.Hostname || device?.sn || device?.uuid || ''
    const tech = device?.Tecnologia || 'Device'
    return `${tech}${name ? ' · ' + name : ''}`
  }

  loadPing (fraz: string): void {
    if (!fraz) {
      return
    }
    this.pingLoading.set(true)
    this.classApi.getRTUPingHistory(fraz, this.pingWindowDays).subscribe({
      next: (data) => {
        this.pingSpans.set(Array.isArray(data) ? data : [])
        this.pingLoading.set(false)
      },
      error: () => {
        this.pingSpans.set([])
        this.pingLoading.set(false)
      }
    })

    this.diagnosisLoading.set(true)
    this.classApi.getRTUPingDiagnostics(fraz).subscribe({
      next: (data) => {
        this.diagnosis.set(data || null)
        this.diagnosisLoading.set(false)
      },
      error: () => {
        this.diagnosis.set(null)
        this.diagnosisLoading.set(false)
      }
    })
  }

  /**
   * Stato tunnel **live**: interroga l'apparato con la stessa logica del
   * "check pianificazione" (FortiManager/vManage). La card KPI è sempre visibile,
   * quindi la query parte solo su richiesta ("Verifica live") per non pesare
   * sull'apertura della sede.
   */
  loadLiveTunnels (force = false): void {
    const fraz = this.siteFraz() || this.fraz()
    if (!fraz || this.liveTunnelsLoading()) {
      return
    }
    if (this.liveTunnels() && !force) {
      return
    }
    this.liveTunnelsLoading.set(true)
    this.api.getLiveTunnels(fraz).subscribe({
      next: (data) => {
        this.liveTunnels.set(data)
        this.liveTunnelsLoading.set(false)
      },
      error: () => {
        this.liveTunnels.set(null)
        this.liveTunnelsLoading.set(false)
      }
    })
  }

  liveTunnelsSourceLabel (): string {
    switch (this.liveTunnels()?.source) {
      case 'fortinet':
        return 'FortiManager'
      case 'cisco':
        return 'vManage'
      default:
        return '—'
    }
  }

  readonly pingWindow = computed(() => {
    const end = Date.now()
    return { start: end - this.pingWindowDays * 86400000, end }
  })

  readonly pingFlow = computed(() => {
    const { start, end } = this.pingWindow()
    const total = Math.max(end - start, 1)
    const segments: { key: string, left: number, width: number, state: string, title: string }[] = []
    this.pingSpans().forEach((span, index) => {
      const begin = Date.parse(span?.inizio)
      if (!Number.isFinite(begin)) {
        return
      }
      const parsedEnd = span?.fine ? Date.parse(span.fine) : end
      const effStart = Math.max(begin, start)
      const effEnd = Math.min(Number.isFinite(parsedEnd) ? parsedEnd : end, end)
      if (effEnd <= effStart) {
        return
      }
      const isOffline = span?.stato === 'offline'
      segments.push({
        key: `${index}-${span?.inizio}`,
        left: ((effStart - start) / total) * 100,
        width: Math.max(((effEnd - effStart) / total) * 100, 0.4),
        state: isOffline ? 'offline' : 'online',
        title: `${isOffline ? 'Offline' : 'Online'} · ${this.formatDate(span?.inizio)} → ${span?.fine ? this.formatDate(span.fine) : 'in corso'} · ${span?.checks || 0} check`
      })
    })
    return segments
  })

  readonly recentSpans = computed(() => [...this.pingSpans()].slice(-8).reverse())

  readonly pingWindowFrom = computed(() => this.formatDateMs(this.pingWindow().start))

  spanInfo (span: any): string {
    const checks = span?.checks ?? 0
    const rtt = span?.avgRttMs != null ? ` · RTT ${Math.round(Number(span.avgRttMs))} ms` : ''
    const loss = span?.packetLoss != null ? ` · loss ${Number(span.packetLoss)}%` : ''
    return `${checks} check${rtt}${loss}`
  }

  spanDuration (span: any): string {
    const start = Date.parse(span?.inizio)
    const end = span?.fine ? Date.parse(span.fine) : Date.now()
    if (!Number.isFinite(start) || !Number.isFinite(end)) {
      return '—'
    }
    return this.formatDuration(Math.max(0, Math.round((end - start) / 1000)))
  }

  formatDuration (seconds: number): string {
    if (!Number.isFinite(seconds) || seconds <= 0) {
      return '0s'
    }
    const days = Math.floor(seconds / 86400)
    const hours = Math.floor((seconds % 86400) / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    if (days > 0) {
      return `${days}g ${hours}h`
    }
    if (hours > 0) {
      return `${hours}h ${minutes}m`
    }
    if (minutes > 0) {
      return `${minutes}m`
    }
    return `${Math.round(seconds)}s`
  }

  formatDateMs (ms: number): string {
    const date = new Date(ms)
    return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString('it-IT')
  }

  /**
   * Chip per un oggetto JSONB di stato tunnel (es. SDWAN_Collaudi.Tunnel_Status).
   * Il backend salva sia `result` (array strutturato) sia `htmlResult`: qui
   * preferiamo sempre i dati strutturati e, in mancanza, convertiamo l'HTML
   * in testo per non mostrarlo crudo a schermo.
   */
  tunnelChips (value: any): { key: string, label: string, value: string, ok: boolean | null }[] {
    const structured = this.tunnelItems(value)
    if (structured.length > 0) {
      return structured.map((tunnel, index) => ({
        key: `${index}-${tunnel.name}`,
        label: tunnel.name,
        value: tunnel.status || '—',
        ok: this.tunnelStatusOk(tunnel.status)
      }))
    }
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return []
    }
    return Object.entries(value)
      .filter(([key]) => !['htmlResult', 'result', 'tunnels', 'tloc'].includes(key))
      .map(([key, raw]) => {
        const label = this.humanizeKey(key)
        if (typeof raw === 'boolean') {
          return { key, label, value: raw ? 'UP' : 'KO', ok: raw }
        }
        const text = this.formatFieldValue(raw)
        return { key, label, value: text || '—', ok: this.tunnelStatusOk(text) }
      })
  }

  /** Lista tunnel normalizzata, pronta per `fq-tunnel-status`. */
  tunnelList (value: any): FqTunnel[] {
    return this.tunnelItems(value).map((tunnel) => ({ name: tunnel.name, status: tunnel.status }))
  }

  hasTunnels (): boolean {
    const overview = this.overview()
    return this.tunnelList(overview?.sdwan?.collaudoFortinet?.tunnel).length > 0 ||
      this.tunnelList(overview?.speedtest?.tunnel).length > 0
  }

  readonly registeredTunnelGroups = computed(() => {
    const overview = this.overview()
    const groups: { key: string, label: string, tunnels: FqTunnel[] }[] = []
    const collaudo = this.tunnelList(overview?.sdwan?.collaudoFortinet?.tunnel)
    if (collaudo.length > 0) {
      groups.push({ key: 'collaudo', label: 'Collaudo Fortinet', tunnels: collaudo })
    }
    const speedtest = this.tunnelList(overview?.speedtest?.tunnel)
    if (speedtest.length > 0) {
      groups.push({ key: 'speedtest', label: 'Ultimo speedtest', tunnels: speedtest })
    }
    return groups
  })

  readonly hasRegisteredTunnels = computed(() => this.registeredTunnelGroups().length > 0)

  tunnelSummary (value: any): string | null {
    if (!value) {
      return null
    }
    const chips = this.tunnelChips(value)
    if (chips.length === 0) {
      if (typeof value === 'object') {
        return null
      }
      const text = stripHtml(String(value))
      return text || null
    }
    const relevant = chips.filter((chip) => chip.ok === false)
    const list = (relevant.length > 0 ? relevant : chips).slice(0, 2)
    return list.map((chip) => `${chip.label}: ${chip.value}`).join(' · ')
  }

  private tunnelItems (value: any): { name: string, status: string }[] {
    if (!value) {
      return []
    }
    if (Array.isArray(value)) {
      return this.mapTunnelItems(value)
    }
    if (typeof value !== 'object') {
      return []
    }
    if (Array.isArray(value.result)) {
      return this.mapTunnelItems(value.result)
    }
    if (Array.isArray(value.tunnels)) {
      return this.mapTunnelItems(value.tunnels)
    }
    if (Array.isArray(value.tloc)) {
      return this.mapTunnelItems(value.tloc)
    }
    if (typeof value.htmlResult === 'string') {
      return this.parseTunnelHtml(value.htmlResult)
    }
    return []
  }

  private mapTunnelItems (items: any[]): { name: string, status: string }[] {
    return items
      .map((item) => {
        if (typeof item === 'string') {
          return { name: item, status: '' }
        }
        const name = item?.name ?? item?.local_color ?? item?.remote_color ?? item?.tunnel ?? ''
        const status = item?.status ?? item?.tloc_state ?? item?.bfd_state ?? item?.state ?? ''
        return { name: String(name ?? '').trim(), status: String(status ?? '').trim() }
      })
      .filter((tunnel) => tunnel.name !== '')
  }

  private parseTunnelHtml (html: string): { name: string, status: string }[] {
    if (typeof DOMParser === 'undefined') {
      return []
    }
    const doc = new DOMParser().parseFromString(html, 'text/html')
    return Array.from(doc.querySelectorAll('tr'))
      .map((row) => {
        const cells = row.querySelectorAll('td')
        if (cells.length < 2) {
          return null
        }
        const name = (cells[0].textContent || '').trim()
        if (!name) {
          return null
        }
        const statusCell = cells[1]
        const marker = `${statusCell.className} ${statusCell.innerHTML}`
        const status = /circle-up|textgreen|fa-circle-up/i.test(marker) ? 'up' : 'down'
        return { name, status }
      })
      .filter((tunnel): tunnel is { name: string, status: string } => tunnel !== null)
  }

  private tunnelStatusOk (status: string | null | undefined): boolean | null {
    const text = String(status ?? '').toLowerCase()
    if (/^(up|ok|connected|active|pass|passed|true)$/.test(text)) {
      return true
    }
    if (/(down|ko|disconnect|fail|false)/.test(text)) {
      return false
    }
    return null
  }

  severityClass (gravita: string | null | undefined): string {
    if (gravita === 'danger') {
      return 'fq-badge-danger'
    }
    if (gravita === 'warning') {
      return 'fq-badge-warning'
    }
    return 'fq-badge-info'
  }

  /** Etichette della catena diagnostica (come nel modal "Diagnostica" della dashboard RTU). */
  readonly diagTargetLabels: Record<string, string> = {
    p2p1: 'P2P primario (MPLS)',
    p2p2: 'P2P backup (MPLS)',
    loopback: 'Firewall / loopback mgmt',
    switch: 'Switch di sede'
  }

  targetLabel (target: string): string {
    return this.diagTargetLabels[target] ?? target
  }

  targetIcon (target: string): any {
    if (target === 'switch') {
      return ['fas', 'sitemap']
    }
    if (target === 'loopback') {
      return ['fas', 'shield-halved']
    }
    return ['fas', 'tower-broadcast']
  }

  targetStatoLabel (stato: string, note?: string | null): string {
    if (note === 'NRD') {
      return 'spento (NRD)'
    }
    if (stato === 'online') {
      return 'raggiungibile'
    }
    if (stato === 'offline') {
      return 'non raggiungibile'
    }
    if (stato === 'skipped') {
      return 'saltato'
    }
    return stato || '—'
  }

  rtuOnline (): boolean {
    return this.overview()?.rtu?.lastPing?.state === 'online'
  }

  diagHeadClass (): string {
    const gravita = this.diagnosis()?.gravita
    if (gravita === 'danger') {
      return 'diag-head-danger'
    }
    if (gravita === 'warning') {
      return 'diag-head-warning'
    }
    return 'diag-head-info'
  }

  diagNodeClass (stato: string | null | undefined, note?: string | null): string {
    if (note === 'NRD' || stato === 'skipped') {
      return 'is-skip'
    }
    if (stato === 'online') {
      return 'is-ok'
    }
    if (stato === 'offline') {
      return 'is-down'
    }
    return ''
  }

  private loadWorkflowLauncherData (): void {
    if (this.workflowLauncherLoaded) return
    this.workflowLauncherLoaded = true
    this.workflowTemplatesLoading.set(true)

    this.subscriptions.add(this.mediationWorkflowApi.getActions().subscribe({
      next: (actions) => {
        setActionCatalog((actions ?? []).filter(action => action.hasExecutor !== false))
        this.workflowCatalogReady.set(true)
      },
      error: () => this.workflowCatalogReady.set(false)
    }))

    this.subscriptions.add(this.mediationWorkflowApi.getTemplates().subscribe({
      next: (templates) => {
        const list = templates ?? []
        this.workflowTemplates.set(list)
        if (!this.selectedWorkflowTemplateId() && list.length) {
          this.selectedWorkflowTemplateId.set(list[0].id)
        }
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Errore', detail: 'Impossibile caricare i template workflow' })
      },
      complete: () => this.workflowTemplatesLoading.set(false)
    }))
  }

  runWorkflowTemplateForSite (): void {
    const fraz = String(this.siteFraz() || '').trim()
    const template = this.workflowTemplates().find(item => item.id === this.selectedWorkflowTemplateId())
    if (!fraz) {
      this.messageService.add({ severity: 'warn', summary: 'Sede non disponibile', detail: 'Seleziona un frazionario.' })
      return
    }
    if (!template) {
      this.messageService.add({ severity: 'warn', summary: 'Template non selezionato', detail: 'Scegli un template da eseguire.' })
      return
    }
    if (!this.workflowCatalogReady()) {
      this.messageService.add({ severity: 'warn', summary: 'Catalogo non pronto', detail: 'Attendi il caricamento delle azioni workflow.' })
      return
    }

    const definition = cloneDefinition(template, [fraz])
    const request = buildRunRequestPayload(definition, {
      runName: `${template.name} - ${fraz}`,
      mode: 'now',
      scheduledAt: null
    })

    this.workflowRunStarting.set(true)
    this.subscriptions.add(this.mediationWorkflowApi.createRun(request).pipe(
      finalize(() => this.workflowRunStarting.set(false))
    ).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Run avviata', detail: `${template.name} su ${fraz}` })
      },
      error: (error) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: error?.error?.error ?? 'Impossibile avviare la run'
        })
      }
    }))
  }

  /** Cambio sezione dalla pill nav: valida la chiave prima di applicarla. */
  onNavChange (key: string): void {
    if (this.tabs().some((item) => item.key === key)) {
      this.setTab(key as HubTab)
    }
  }

  setTab (tab: HubTab): void {
    this.tab.set(tab)
    if (tab === 'perimetro' || tab === 'circuiti' || tab === 'dispositivi') {
      this.loadVoipDetails()
    }
    if (this.embedded()) {
      return
    }
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab },
      queryParamsHandling: 'merge',
      replaceUrl: true
    })
  }

  refresh (): void {
    this.load(this.fraz())
  }

  copyLink (): void {
    const url = this.embedded()
      ? `${window.location.origin}/awp/sites/${encodeURIComponent(this.siteFraz())}`
      : window.location.href
    const done = () => this.messageService.add({ severity: 'success', summary: 'Sede', detail: 'Link copiato' })
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(done).catch(() => { /* clipboard non disponibile */ })
    }
  }

  openTicket (id: number): void {
    if (!this.canManage()) {
      return
    }
    void this.router.navigate(['/support/manage'], { queryParams: { ticket: id } })
  }

  timelineLink (): string {
    return `/awp/site-timeline?fraz=${encodeURIComponent(this.siteFraz())}`
  }

  perimetroLink (): string {
    return `/perimetro-awp?fraz=${encodeURIComponent(this.siteFraz())}`
  }

  rtuLink (): string {
    return `/rtu-dashboard?q=${encodeURIComponent(this.siteFraz())}`
  }

  formatDate (value: string | null | undefined): string {
    if (!value) {
      return '—'
    }
    const date = new Date(value)
    return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' })
  }

  formatDateOnly (value: string | null | undefined): string {
    if (!value) {
      return '—'
    }
    const date = new Date(value)
    return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString('it-IT')
  }

  /** Valore Mbps leggibile (stringhe numeriche incluse). */
  formatSpeed (value: number | string | null | undefined): string {
    if (value === null || value === undefined || value === '') {
      return '—'
    }
    const speed = Number(value)
    return Number.isFinite(speed) ? String(Math.round(speed * 10) / 10) : '—'
  }

  /** Valore generico (booleano, stringa, oggetto) pronto per l'interfaccia. */
  displayValue (value: any): string {
    if (value === null || value === undefined || value === '') {
      return '—'
    }
    if (typeof value === 'boolean') {
      return value ? 'Sì' : 'No'
    }
    if (typeof value === 'object') {
      return this.formatFieldValue(value)
    }
    return stripHtml(String(value))
  }

  /** Testo libero (note, errori) senza markup HTML. */
  cleanText (value: any): string {
    return value === null || value === undefined ? '' : stripHtml(String(value))
  }

  statusClass (status: string | null | undefined): string {
    const text = String(status || '').toLowerCase()
    if (/(fail|error|errore|offline|ko|cessat)/.test(text)) {
      return 'is-error'
    }
    if (/(warn|progress|running|pending|parziale|annull|in fase)/.test(text)) {
      return 'is-warning'
    }
    if (/(complet|success|ok|online|done|attiv|installata|installato|migrata|migrato)/.test(text)) {
      return 'is-success'
    }
    return 'is-info'
  }

  ticketStatusLabel (status: string | null | undefined): string {
    switch (status) {
      case 'in_progress':
        return 'In progress'
      case 'done':
        return 'Done'
      case 'closed':
        return 'Closed'
      default:
        return 'Open'
    }
  }

  ticketBadgeClass (status: string | null | undefined): string {
    switch (status) {
      case 'done':
        return 'fq-badge-success'
      case 'closed':
        return 'fq-badge-secondary'
      case 'in_progress':
        return 'fq-badge-warning'
      default:
        return 'fq-badge-danger'
    }
  }

  /** Campi Metafield in coppie etichetta/valore, escluse le chiavi tecniche. */
  metafieldEntries (fields: Record<string, any> | null | undefined): { key: string, label: string, value: string, bool: boolean | null, multiline: boolean }[] {
    if (!fields) {
      return []
    }
    return Object.entries(fields).map(([key, value]) => {
      const formatted = this.formatFieldValue(value)
      return {
        key,
        label: this.humanizeKey(key),
        value: formatted,
        bool: typeof value === 'boolean' ? value : null,
        multiline: typeof value === 'object' || /[\n\r]/.test(formatted)
      }
    })
  }

  /** true se il metafield ha almeno un campo VLAN valorizzato. */
  readonly hasVlanFields = computed(() => {
    const fields = this.overview()?.metafield?.fields
    if (!fields) {
      return false
    }
    return Object.keys(fields).some((key) =>
      isVlanMetafieldName(key) && String(fields[key] ?? '').trim() !== ''
    )
  })

  readonly metafieldOtherEntries = computed(() =>
    this.metafieldEntries(this.overview()?.metafield?.fields)
      .filter((field) => !isVlanMetafieldName(field.key))
  )

  private humanizeKey (key: string): string {
    return key
      .replace(/_/g, ' ')
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/^./, (char) => char.toUpperCase())
  }

  private formatFieldValue (value: any): string {
    if (value === null || value === undefined) {
      return '—'
    }
    if (typeof value === 'boolean') {
      return value ? 'Sì' : 'No'
    }
    if (typeof value === 'string') {
      const text = containsHtml(value) ? stripHtml(value) : value
      return this.truncate(text.trim() || '—')
    }
    if (typeof value === 'object') {
      try {
        const text = Array.isArray(value) && value.every((item) => item === null || typeof item !== 'object')
          ? value.map((item) => String(item)).join(', ')
          : JSON.stringify(value, null, 2)
        return this.truncate(text)
      } catch {
        return String(value)
      }
    }
    return String(value)
  }

  private truncate (value: string, max = 2000): string {
    return value.length > max ? value.slice(0, max) + '…' : value
  }
}
